import React, { useRef, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card from "@react/shared/ui/Card"

const CELL     = "w-full h-9 px-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition"
const TEXTAREA = "w-full px-2 py-1.5 rounded-md border border-slate-200 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition"
const TH       = "px-2 pb-2 text-[10.5px] uppercase tracking-wider text-slate-400 font-medium"
const ICON_BTN = "w-7 h-7 grid place-items-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"

const formatAmount = (cents, symbol = "€") =>
  `${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`

const lineHtCents = line => {
  if (line.is_page_break) return 0
  const quantity  = parseFloat(line.quantity)  || 0
  const unitPrice = parseFloat(line.unit_price) || 0
  const discount  = parseFloat(line.discount)   || 0
  return Math.round(quantity * unitPrice * (1 - discount / 100) * 100)
}

const AutoGrowTextarea = ({ value, placeholder, onChange }) => {
  const ref = useRef(null)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    element.style.height = "auto"
    element.style.height = `${element.scrollHeight}px`
  }, [value])

  return (
    <textarea
      ref         = {ref}
      rows        = {1}
      value       = {value}
      placeholder = {placeholder}
      onChange    = {event => onChange(event.target.value)}
      className   = {TEXTAREA}
      style       = {{ resize: "none", overflow: "hidden", minHeight: 36 }}
    />
  )
}

const RowActions = ({ line, index, total, onRemove, onMove }) => (
  <div className="flex items-center justify-end">
    <button type="button" className={ICON_BTN} onClick={() => onMove(line._uid, -1)} disabled={index === 0}><FontAwesomeIcon icon="chevron-up" /></button>
    <button type="button" className={ICON_BTN} onClick={() => onMove(line._uid, 1)} disabled={index === total - 1}><FontAwesomeIcon icon="chevron-down" /></button>
    <button type="button" className={`${ICON_BTN} hover:text-rose-600`} onClick={() => onRemove(line._uid)}><FontAwesomeIcon icon="trash" /></button>
  </div>
)

const LineRow = ({ line, index, total, vatRates, billingCategories, showBillingCategory, currencySymbol, onChange, onVatChange, onRemove, onMove }) => {
  if (line.is_page_break) {
    return (
      <tr className="border-t border-slate-100">
        <td colSpan={showBillingCategory ? 8 : 7} className="px-2 py-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-slate-300 bg-slate-50 text-[12px] text-slate-500">
            <FontAwesomeIcon icon="scissors" />
            {I18n.t("projects.invoices.builder.page_break")}
          </div>
        </td>
        <td className="px-2 py-2 align-middle"><RowActions line={line} index={index} total={total} onRemove={onRemove} onMove={onMove} /></td>
      </tr>
    )
  }

  if (line.is_descriptive) {
    return (
      <tr className="border-t border-slate-100 align-top">
        <td colSpan={showBillingCategory ? 8 : 7} className="px-2 py-1.5">
          <AutoGrowTextarea value={line.description} placeholder={I18n.t("projects.invoices.builder.descriptive_placeholder")} onChange={value => onChange(line._uid, { description: value })} />
        </td>
        <td className="px-2 py-1.5"><RowActions line={line} index={index} total={total} onRemove={onRemove} onMove={onMove} /></td>
      </tr>
    )
  }

  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-2 py-1.5">
        <AutoGrowTextarea value={line.description} placeholder={I18n.t("projects.invoices.builder.description")} onChange={value => onChange(line._uid, { description: value })} />
      </td>
      <td className="px-2 py-1.5">
        <input type="number" step="0.01" className={`${CELL} text-right tabular-nums`} value={line.quantity} onChange={event => onChange(line._uid, { quantity: event.target.value })} />
      </td>
      <td className="px-2 py-1.5">
        <input className={`${CELL} text-center`} value={line.unit || ""} onChange={event => onChange(line._uid, { unit: event.target.value })} />
      </td>
      <td className="px-2 py-1.5">
        <input type="number" step="0.01" className={`${CELL} text-right tabular-nums`} value={line.unit_price} onChange={event => onChange(line._uid, { unit_price: event.target.value })} />
      </td>
      <td className="px-2 py-1.5">
        <input type="number" step="0.01" min="0" max="100" className={`${CELL} text-right tabular-nums`} value={line.discount} onChange={event => onChange(line._uid, { discount: event.target.value })} />
      </td>
      <td className="px-2 py-1.5">
        <select className={`${CELL} bg-white`} value={line.vat_rate_id || ""} onChange={event => onVatChange(line._uid, event.target.value)}>
          <option value="">—</option>
          {vatRates.map(rate => <option key={rate.id} value={rate.id}>{rate.label}</option>)}
        </select>
      </td>
      {showBillingCategory && (
        <td className="px-2 py-1.5">
          <select className={`${CELL} bg-white`} value={line.billing_category_id || ""} onChange={event => onChange(line._uid, { billing_category_id: event.target.value })}>
            <option value="">—</option>
            {billingCategories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
        </td>
      )}
      <td className="px-2 py-1.5 text-right tabular-nums text-[13px] font-medium text-slate-800 whitespace-nowrap">
        {formatAmount(lineHtCents(line), currencySymbol)}
      </td>
      <td className="px-2 py-1.5">
        <RowActions line={line} index={index} total={total} onRemove={onRemove} onMove={onMove} />
      </td>
    </tr>
  )
}

const LinesTable = ({ lines, vatRates, billingCategories = [], showBillingCategory, currencySymbol, onLineChange, onLineVatChange, onAddLine, onRemoveLine, onMoveLine }) => {
  const colWidths = showBillingCategory
    ? [null, 84, 76, 120, 80, 140, 150, 120, 72]
    : [null, 88, 78, 128, 88, 150, 128, 84]
  const totalCols = colWidths.length

  return (
    <Card className="overflow-hidden">
      <div className="px-5 pt-5 pb-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.invoices.builder.lines")}</h2>
      </div>

      <div className="px-3 overflow-x-auto">
        <table className="w-full" style={{ tableLayout: "fixed", minWidth: showBillingCategory ? 880 : 720 }}>
          <colgroup>
            {colWidths.map((width, index) => <col key={index} style={width ? { width } : undefined} />)}
          </colgroup>
          <thead>
            <tr className="text-left border-b border-slate-200">
              <th className={TH}>{I18n.t("projects.invoices.builder.col.description")}</th>
              <th className={`${TH} text-right`}>{I18n.t("projects.invoices.builder.col.quantity")}</th>
              <th className={`${TH} text-center`}>{I18n.t("projects.invoices.builder.col.unit")}</th>
              <th className={`${TH} text-right`}>{I18n.t("projects.invoices.builder.col.unit_price")}</th>
              <th className={`${TH} text-right`}>{I18n.t("projects.invoices.builder.col.discount")}</th>
              <th className={TH}>{I18n.t("projects.invoices.builder.col.vat")}</th>
              {showBillingCategory && <th className={TH}>{I18n.t("billing_categories.billing_category_short")}</th>}
              <th className={`${TH} text-right`}>{I18n.t("projects.invoices.builder.col.total_ht")}</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={totalCols} className="py-10 text-center text-[13px] text-slate-400">{I18n.t("projects.invoices.builder.empty")}</td></tr>
            ) : lines.map((line, index) => (
              <LineRow
                key                 = {line._uid}
                line                = {line}
                index               = {index}
                total               = {lines.length}
                vatRates            = {vatRates}
                billingCategories   = {billingCategories}
                showBillingCategory = {showBillingCategory}
                currencySymbol      = {currencySymbol}
                onChange            = {onLineChange}
                onVatChange         = {onLineVatChange}
                onRemove            = {onRemoveLine}
                onMove              = {onMoveLine}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-4 flex items-center gap-3">
        <button type="button" onClick={() => onAddLine()} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium text-blue-700 hover:bg-blue-50 rounded-md border border-blue-200">
          <FontAwesomeIcon icon="plus" /> {I18n.t("projects.invoices.builder.add_line")}
        </button>
        <button type="button" onClick={() => onAddLine({ is_descriptive: true })} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-100 rounded-md border border-slate-200">
          <FontAwesomeIcon icon="align-left" /> {I18n.t("projects.invoices.builder.add_descriptive")}
        </button>
        <button type="button" onClick={() => onAddLine({ is_page_break: true })} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-100 rounded-md border border-slate-200">
          <FontAwesomeIcon icon="scissors" /> {I18n.t("projects.invoices.builder.add_page_break")}
        </button>
      </div>
    </Card>
  )
}

export default LinesTable
